import { ReactElement } from "react";

import { camelCaseToSentenceCase, getDateFromValue } from "../../../../utils";
import { Column, Row } from "../../../layout";
import { DATE_ONLY_PATTERN } from "../constants";
import { DynamicJsonSchemaPrimitiveType, DynamicJsonSchemaProperty, DynamicRenderedField } from "../models";

export { getDateFromValue };

export const hasSchemaType = (
  definition: DynamicJsonSchemaProperty,
  schemaType: DynamicJsonSchemaPrimitiveType
): boolean => {
  if (Array.isArray(definition.type)) {
    return definition.type.includes(schemaType);
  }

  return definition.type === schemaType;
};

/**
 * True when a schema property definition is safe to render/inspect — a non-null object with at least
 * one key. Optional fields a schema omits can arrive as `undefined`, `null`, or `{}`, so this guards
 * the other predicates (e.g. {@link isDateField}) from dereferencing a non-object definition.
 */
export const isRenderableFieldDefinition = (definition: unknown): definition is DynamicJsonSchemaProperty =>
  typeof definition === "object" && definition !== null && Object.keys(definition).length > 0;

export const isDateField = (definition: DynamicJsonSchemaProperty): boolean => {
  const hasDatePattern =
    definition.pattern === DATE_ONLY_PATTERN || definition.pattern?.includes("\\d{4}-\\d{2}-\\d{2}");

  return hasSchemaType(definition, "string") && (definition.format === "date" || hasDatePattern === true);
};

export const isNumberField = (definition: DynamicJsonSchemaProperty): boolean => {
  return hasSchemaType(definition, "number") || hasSchemaType(definition, "integer");
};

export const isArrayEnumField = (definition: DynamicJsonSchemaProperty): boolean => {
  return hasSchemaType(definition, "array") && definition.items?.enum !== undefined;
};

export const isTableRepeaterField = (definition: DynamicJsonSchemaProperty): boolean => {
  return hasSchemaType(definition, "array") && definition.component?.toLowerCase() === "tablerepeater";
};

export const isObjectField = (definition: DynamicJsonSchemaProperty): boolean => {
  return hasSchemaType(definition, "object");
};

export const isParagraphField = (definition: DynamicJsonSchemaProperty): boolean => {
  return definition.component?.toLowerCase() === "paragraph";
};

export const normaliseSchemaFieldLabel = (fieldKey: string): string => {
  return camelCaseToSentenceCase(fieldKey);
};

export const renderFieldsInTwoColumnRows = (fields: DynamicRenderedField[]): ReactElement[] => {
  const rows: ReactElement[] = [];
  let index = 0;

  while (index < fields.length) {
    const leftField = fields[index];

    if (leftField.isFullWidth) {
      rows.push(
        <Row spacingV="ll" key={leftField.key}>
          <Column span={12}>{leftField.element}</Column>
        </Row>
      );
      index += 1;
    } else {
      const rightField = fields[index + 1] && !fields[index + 1].isFullWidth ? fields[index + 1] : undefined;

      rows.push(
        <Row spacingV="ll" key={`${leftField.key}-${rightField?.key ?? "single"}`}>
          <Column span={4}>{leftField.element}</Column>
          {rightField ? (
            <Column offset="1_half" span={4}>
              {rightField.element}
            </Column>
          ) : undefined}
        </Row>
      );
      index += rightField ? 2 : 1;
    }
  }

  return rows;
};

export const normaliseExpressionContextValue = <TProperty extends DynamicJsonSchemaProperty>(
  fieldDefinition: TProperty,
  value: unknown
): unknown => {
  if (!isNumberField(fieldDefinition) || typeof value !== "string") {
    return value;
  }

  const trimmedValue = value.trim();
  if (trimmedValue === "") {
    return value;
  }

  const parsedValue = Number(trimmedValue);
  return Number.isNaN(parsedValue) ? value : parsedValue;
};
